import { useCallback, useState } from "react";
import {
  bookRide,
  buildTravellers,
  createBookingTransaction,
  type BookRideResult,
} from "@/services/rideBookingService";
import type { RideApiSeat } from "@/types/rideDetails";

export type RideBookingStatus = "idle" | "booking" | "success" | "error";

export interface RunBookingParams {
  /** Numeric ride id (rideOffer.rid). */
  rid: number;
  seating: RideApiSeat[];
  seatCount: number;
  guestName: string;
  gender: string;
  pickupStopId: number;
  dropStopId: number;
  totalPrice: number;
}

/**
 * Orchestrates the two-step booking flow:
 *   1. publish_or_book_ride  -> reserves the seats and returns a booking id/number.
 *   2. CreateTransaction     -> records the (cash) payment for that booking.
 */
export function useRideBooking() {
  const [status, setStatus] = useState<RideBookingStatus>("idle");
  const [result, setResult] = useState<BookRideResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runBooking = useCallback(async (params: RunBookingParams) => {
    setStatus("booking");
    setError(null);

    try {
      const travellers = buildTravellers({
        seating: params.seating,
        seatCount: params.seatCount,
        guestName: params.guestName,
        gender: params.gender,
        pickupStopId: params.pickupStopId,
        dropStopId: params.dropStopId,
      });

      if (travellers.length === 0) {
        throw new Error("No available seats to book.");
      }

      const bookingResult = await bookRide({ rid: params.rid, travellers });
      setResult(bookingResult);

      await createBookingTransaction({
        rid: params.rid,
        bookingId: bookingResult.bookingId,
        totalPrice: params.totalPrice,
        seatCount: params.seatCount,
      });

      setStatus("success");
      return bookingResult;
    } catch (e: any) {
      setError(e?.message || "Something went wrong while booking. Please try again.");
      setStatus("error");
      return null;
    }
  }, []);

  return { status, result, error, runBooking };
}
